import { Fragment, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { wabaApi } from "../../lib/wabaApi";

const TOGGLES = [
  { field: "auto_split_text", label: "Separar texto automaticamente", desc: "Divide a resposta em múltiplas mensagens no \\n\\n" },
  { field: "auto_process_links", label: "Processar links de mídia", desc: "Envia imagens, vídeos e áudios diretamente pelo URL" },
  { field: "auto_carousel", label: "Carrossel de imagens", desc: "2 a 10 imagens na resposta viram um carrossel deslizável (imagem + legenda + botões abaixo). Requer “Processar links”." },
  { field: "burst_carousel", label: "Carrossel para mais fotos do veículo", desc: "2 ou mais links de foto seguidos, sem legenda, viram um carrossel com só a foto e o botão (até 10; as fotos a mais são descartadas). O texto antes das fotos vai junto no carrossel; o texto depois sai em seguida. Requer “Processar links”." },
  { field: "auto_patioo_cta", label: "Link do Patioo vira botão", desc: "Texto com link patioo.com.br sai como mensagem com botão “Ver veículos” que abre a seleção. O texto vai no corpo, o link só no botão." },
  { field: "typing_indicator", label: "Mostrar “digitando”", desc: "Exibe “digitando…” por 3s antes do texto da IA/CRM. Mídia vai direto." },
  { field: "auto_mark_read", label: "Marcar recebidas como lidas", desc: "Toda mensagem recebida ganha ✓✓ azul imediatamente ao ser processada." },
  { field: "message_spacing", label: "Espaçamento entre mensagens", desc: "Aguarda 3–5s (aleatório) entre cada mensagem quando a resposta é dividida em várias. Desligado = sem pausa." },
  { field: "send_via_bsuid", label: "Envio via BSUID", desc: "Lead com BSUID recebe pelo BSUID (campo recipient) em vez do telefone. Lead sem BSUID segue pelo telefone. Teste: confira no log se o status chega como entregue." },
] as const;

// Instagram (colunas ig_*): mesmos papéis, formatos do Direct. Patioo fica de fora.
const IG_TOGGLES = [
  { field: "ig_auto_split_text", label: "Separar texto automaticamente", desc: "Divide a resposta em múltiplas mensagens no \\n\\n. O Instagram recusa mensagem com 1000+ caracteres." },
  { field: "ig_auto_process_links", label: "Processar links de mídia", desc: "Envia imagens (jpg/png), vídeos (mp4/mov), áudios (m4a/wav) e PDFs pelo URL. Fotos seguidas sem legenda vão numa mensagem só (até 10). Outros formatos seguem como link." },
  { field: "ig_auto_carousel", label: "Carrossel de imagens", desc: "2 a 10 imagens com legenda viram um carrossel (imagem + legenda + botões abaixo). Os botões são os mesmos do carrossel do WhatsApp (lista compartilhada). Não aparece no Instagram web. Requer “Processar links” e o campo messaging_postbacks assinado no app." },
  { field: "ig_typing_indicator", label: "Mostrar “digitando”", desc: "Exibe “digitando…” enquanto a IA prepara a resposta." },
  { field: "ig_auto_mark_read", label: "Marcar recebidas como vistas", desc: "Toda DM recebida aparece como vista assim que é processada." },
  { field: "ig_message_spacing", label: "Espaçamento entre mensagens", desc: "Aguarda 3–5s (aleatório) entre cada mensagem quando a resposta é dividida em várias. Desligado = sem pausa." },
] as const;

// Messenger: só o carrossel; o resto do texto sai como veio.
const MSN_TOGGLES = [
  { field: "messenger_auto_carousel", label: "Carrossel de imagens", desc: "2 a 10 links de imagem, cada um com legenda, viram um carrossel (imagem + legenda + botões abaixo). Os botões são os mesmos do carrossel do WhatsApp (lista compartilhada). Sem carrossel, o texto sai como veio, com os links." },
] as const;

type ToggleField = (typeof TOGGLES)[number]["field"] | (typeof IG_TOGGLES)[number]["field"] | (typeof MSN_TOGGLES)[number]["field"];

const DEFAULT_BUTTONS = ["Gostei deste", "Simular"];

function ButtonsEditor({ conn, onChanged, field = "buttons" }: {
  conn: any; onChanged: () => void | Promise<void>; field?: "buttons" | "burst_buttons";
}) {
  const qc = useQueryClient();
  // Carrossel manda no máximo 2: um 3º salvo antes some no próximo "Salvar".
  const [titles, setTitles] = useState<string[]>(
    (conn[field]?.length ? conn[field] : DEFAULT_BUTTONS).slice(0, 2)
  );
  const [saving, setSaving] = useState(false);

  const clean = titles.map(t => t.trim()).filter(Boolean);
  const valid = clean.length >= 1 && clean.length <= 2;

  async function save() {
    setSaving(true);
    try {
      await wabaApi.patch(`/connections/${conn.id}`, { [field]: clean });
      qc.invalidateQueries({ queryKey: ["waba-connections"] });
      await onChanged();
    } finally {
      setSaving(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    flex: 1, padding: "6px 8px", background: "var(--surface-raised)", border: "1px solid var(--line-strong)",
    borderRadius: 6, color: "var(--ink)", fontSize: 12, outline: "none",
  };

  return (
    <div style={{ padding: "10px 12px", background: "var(--surface-raised)", border: "1px solid var(--line)", borderRadius: 8, display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: 11, color: "var(--ink-muted)" }}>Botões do carrossel (1 a 2, até 20 caracteres cada)</div>
      {titles.map((t, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            value={t}
            maxLength={20}
            onChange={e => setTitles(arr => arr.map((x, j) => (j === i ? e.target.value : x)))}
            placeholder={`Botão ${i + 1}`}
            style={inputStyle}
          />
          {titles.length > 1 && (
            <button
              onClick={() => setTitles(arr => arr.filter((_, j) => j !== i))}
              title="Remover"
              style={{ background: "none", border: "1px solid var(--line-strong)", borderRadius: 6, color: "var(--ink-muted)", cursor: "pointer", fontSize: 14, lineHeight: 1, padding: "4px 8px" }}
            >×</button>
          )}
        </div>
      ))}
      <div style={{ display: "flex", gap: 8 }}>
        {titles.length < 2 && (
          <button
            onClick={() => setTitles(arr => [...arr, ""])}
            style={{ background: "transparent", border: "1px dashed var(--line-bold)", borderRadius: 6, color: "var(--ink-secondary)", fontSize: 12, padding: "5px 10px", cursor: "pointer" }}
          >+ Adicionar botão</button>
        )}
        <button
          onClick={save}
          disabled={saving || !valid}
          style={{ marginLeft: "auto", background: valid ? "var(--brand)" : "var(--line-bold)", border: "none", borderRadius: 6, color: "#fff", fontSize: 12, padding: "5px 14px", cursor: saving || !valid ? "default" : "pointer" }}
        >{saving ? "Salvando..." : "Salvar botões"}</button>
      </div>
    </div>
  );
}

export function SendBehaviorToggles({ conn, onChanged, channel }: {
  conn: any; onChanged: () => void | Promise<void>; channel?: "instagram" | "messenger";
}) {
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const list: readonly { field: ToggleField; label: string; desc: string }[] =
    channel === "instagram" ? IG_TOGGLES : channel === "messenger" ? MSN_TOGGLES : TOGGLES;

  async function toggle(field: ToggleField, current: boolean) {
    setSaving(true);
    try {
      await wabaApi.patch(`/connections/${conn.id}`, { [field]: !current });
      qc.invalidateQueries({ queryKey: ["waba-connections"] });
      await onChanged();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {list.map(({ field, label, desc }) => {
        const on = conn[field];
        // Editor dos botões logo abaixo do carrossel ligado. Lista única por
        // conexão: editar no WhatsApp ou no Instagram muda os dois.
        const carousel = field === "auto_carousel" || field === "ig_auto_carousel" || field === "messenger_auto_carousel";
        return (
          <Fragment key={field}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: "var(--surface-raised)", border: "1px solid var(--line)", borderRadius: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, color: "var(--ink)" }}>{label}</div>
              <div style={{ fontSize: 11, color: "var(--ink-muted)", marginTop: 2 }}>{desc}</div>
            </div>
            <button
              disabled={saving}
              onClick={() => toggle(field, on)}
              style={{
                width: 40, height: 22, borderRadius: 99, border: "none",
                background: on ? "var(--brand)" : "var(--line-bold)",
                cursor: saving ? "wait" : "pointer",
                position: "relative", flexShrink: 0, transition: "background 0.2s",
              }}
            >
              <span style={{
                position: "absolute", top: 3, width: 16, height: 16,
                left: on ? 21 : 3, borderRadius: "50%", background: "#fff",
                transition: "left 0.2s",
              }} />
            </button>
          </div>
          {carousel && on && <ButtonsEditor conn={conn} onChanged={onChanged} />}
          {/* Rajada tem lista própria: não muda os botões do carrossel normal. */}
          {field === "burst_carousel" && on && <ButtonsEditor conn={conn} onChanged={onChanged} field="burst_buttons" />}
          </Fragment>
        );
      })}
    </div>
  );
}
